'use client'

import { useEffect, useState, useId, useRef } from 'react'
import { Copy, Check, Image, X } from 'lucide-react'
import { copySvgAsImage, MEDIA_PREVIEW_HEIGHT } from '@/lib/image-utils'
import { MediaPreview } from './media-preview'

interface MermaidDiagramProps {
  code: string
}

const DIAGRAM_HEIGHT = MEDIA_PREVIEW_HEIGHT
const DIAGRAM_CONTENT_HEIGHT = DIAGRAM_HEIGHT - 24 // Account for p-3 padding (12px * 2)

// Make SVG responsive for inline display - fits within fixed height while filling width
function makeResponsiveSvg(svgString: string, height: number): string {
  const widthMatch = svgString.match(/width="([^"]+)"/)
  const heightMatch = svgString.match(/height="([^"]+)"/)

  if (widthMatch && heightMatch) {
    const width = parseFloat(widthMatch[1])
    const h = parseFloat(heightMatch[1])

    if (!svgString.includes('viewBox')) {
      svgString = svgString.replace(
        '<svg',
        `<svg viewBox="0 0 ${width} ${h}"`
      )
    }

    // Scale to fit within fixed height while constraining width
    svgString = svgString
      .replace(/width="[^"]+"/, `style="max-width:100%;height:${height}px;object-fit:contain"`)
      .replace(/height="[^"]+"/, '')
  }

  return svgString
}

// Detect if dark mode is active
function isDarkMode(): boolean {
  if (typeof window === 'undefined') return true
  return document.documentElement.classList.contains('dark')
}

export function MermaidDiagram({ code }: MermaidDiagramProps) {
  const [svg, setSvg] = useState<string | null>(null)
  const [responsiveSvg, setResponsiveSvg] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copiedCode, setCopiedCode] = useState(false)
  const [copiedImage, setCopiedImage] = useState(false)
  const [copyError, setCopyError] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const id = useId().replace(/:/g, '')
  const renderCountRef = useRef(0)

  useEffect(() => {
    let cancelled = false
    let tempContainer: HTMLDivElement | null = null

    async function render() {
      try {
        const mermaid = (await import('mermaid')).default
        const darkMode = isDarkMode()
        mermaid.initialize({
          startOnLoad: false,
          theme: darkMode ? 'dark' : 'default',
          securityLevel: 'strict',
          fontFamily: 'ui-sans-serif, system-ui, sans-serif',
          flowchart: {
            nodeSpacing: 50,
            rankSpacing: 50,
            padding: 15,
            useMaxWidth: false,
          },
          sequence: {
            useMaxWidth: false,
          },
        })

        tempContainer = document.createElement('div')
        tempContainer.style.cssText = 'position:absolute;left:-9999px;width:1000px;min-width:1000px;'
        document.body.appendChild(tempContainer)

        renderCountRef.current += 1
        const renderId = `mermaid-${id}-${renderCountRef.current}`
        const { svg: renderedSvg } = await mermaid.render(renderId, code, tempContainer)

        if (!cancelled) {
          setSvg(renderedSvg)
          setResponsiveSvg(makeResponsiveSvg(renderedSvg, DIAGRAM_CONTENT_HEIGHT))
          setError(null)
          // Dispatch event to trigger scroll after async render
          window.dispatchEvent(new CustomEvent('mermaid-rendered'))
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to render diagram')
          setSvg(null)
          setResponsiveSvg(null)
        }
      } finally {
        if (tempContainer && document.body.contains(tempContainer)) {
          document.body.removeChild(tempContainer)
        }
      }
    }

    render()
    return () => {
      cancelled = true
    }
  }, [code, id])

  const handleCopyCode = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(code)
      setCopiedCode(true)
      setTimeout(() => setCopiedCode(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleCopyImage = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!svg) return

    const backgroundColor = isDarkMode() ? '#18181b' : '#ffffff'
    const result = await copySvgAsImage(svg, { backgroundColor })
    if (result.success) {
      setCopiedImage(true)
      setTimeout(() => setCopiedImage(false), 2000)
    } else {
      setCopyError(true)
      setTimeout(() => setCopyError(false), 2000)
    }
  }

  if (error) {
    return (
      <div className="max-w-full rounded-lg overflow-hidden">
        <div className="bg-red-500/10 text-red-500 text-xs px-3 py-1.5 border-b border-red-500/20 flex items-center justify-between">
          <span>Mermaid error: {error}</span>
          <button
            onClick={handleCopyCode}
            className="p-1 hover:bg-red-500/20 rounded transition-colors"
            title="Copy code"
          >
            {copiedCode ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
          </button>
        </div>
        <pre className="overflow-x-auto rounded-b-lg bg-zinc-950 dark:bg-zinc-900 p-3 text-sm">
          <code className="text-zinc-100 font-mono text-sm">{code}</code>
        </pre>
      </div>
    )
  }

  if (!svg) {
    return (
      <div
        className="flex items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800/50 text-muted-foreground text-sm"
        style={{ height: DIAGRAM_HEIGHT }}
      >
        <svg className="animate-spin h-4 w-4 mr-2" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
        Rendering diagram...
      </div>
    )
  }

  return (
    <>
      <div className="max-w-full rounded-lg overflow-hidden bg-zinc-100 dark:bg-zinc-800/50">
        <div className="flex items-center justify-between gap-1 px-2 py-1.5 border-b border-zinc-200 dark:border-zinc-700/50">
          <span className="text-xs text-muted-foreground">Mermaid</span>
          <div className="flex items-center gap-1">
            <button
              onClick={handleCopyImage}
              className="p-1.5 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded transition-colors text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              title="Copy image"
            >
              {copyError ? <X className="h-4 w-4 text-red-500" /> : copiedImage ? <Check className="h-4 w-4 text-green-500" /> : <Image className="h-4 w-4" />}
            </button>
            <button
              onClick={handleCopyCode}
              className="p-1.5 hover:bg-zinc-200 dark:hover:bg-zinc-700 rounded transition-colors text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              title="Copy code"
            >
              {copiedCode ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={() => setIsPreviewOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              setIsPreviewOpen(true)
            }
          }}
          className="cursor-pointer hover:bg-zinc-200/50 dark:hover:bg-zinc-700/30 transition-colors outline-none flex items-center justify-center p-3"
          style={{ height: DIAGRAM_HEIGHT }}
          title="Click to zoom"
          aria-label="Open diagram in fullscreen"
        >
          <div
            className="[&_svg]:block [&_svg]:mx-auto [&_svg]:outline-none [&_svg]:border-none max-h-full max-w-full overflow-hidden"
            dangerouslySetInnerHTML={{ __html: responsiveSvg || '' }}
          />
        </div>
      </div>

      <MediaPreview
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        svgContent={svg}
        title="Mermaid Diagram Preview"
      />
    </>
  )
}
